// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — turning a seed into the pair sequence, deterministically. This is the
// WHOLE P2P-sync surface: every peer feeds the frozen lobby seed through here and
// gets the byte-identical sequence, so no pair ever crosses the wire. Each pair is a
// pure function of (mode, seed, position); an alive peer has answered every prior
// pair correctly, so alive peers always share the same next pair.

import { makeRng, randInt, hashSeed, type Rng } from '@ben-gy/game-engine/rng';
import { PLAY_POOL, STATS, statValue, type Country, type StatId } from './data';
import { MAX_PAIRS, ratioBand, statForPair, type Mode } from './modes';

/** One "which is bigger?" question. leftVal/rightVal are the true stat values. */
export interface Pair {
  stat: StatId;
  leftId: string;
  rightId: string;
  leftVal: number;
  rightVal: number;
}

/** Today's date in UTC as YYYY-MM-DD — the same string for every player on Earth. */
export function todayUTC(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

/** The daily seed for a mode: identical worldwide, changes at UTC midnight. */
export function dailySeed(mode: Mode, dateStr: string): number {
  return hashSeed(`outsize:${mode.id}:${dateStr}`);
}

const RECENT = 24; // avoid reusing a country within this many pairs

function ratio(a: number, b: number): number {
  return a >= b ? a / b : b / a;
}

/** Choose a partner for A whose stat value sits in the ratio band; always valid. */
function pickPartner(
  a: Country,
  valA: number,
  stat: StatId,
  rMin: number,
  rMax: number,
  exclude: ReadonlySet<string>,
  rng: Rng,
): Country {
  const inBand: Country[] = [];
  let bestBackup: Country | null = null;
  let bestBackupR = Infinity;
  const floor = STATS[stat].floor;
  for (const b of PLAY_POOL) {
    if (b.id === a.id || exclude.has(b.id)) continue;
    const vb = statValue(b, stat);
    if (vb <= 0) continue;
    const r = ratio(valA, vb);
    if (r >= rMin && r <= rMax) inBand.push(b);
    // Backup: the closest partner that still clears the floor, in case the band is
    // empty (A near an extreme value). Guarantees a valid, never-a-tie pair.
    if (r >= floor && r < bestBackupR) {
      bestBackupR = r;
      bestBackup = b;
    }
  }
  if (inBand.length) return inBand[randInt(rng, 0, inBand.length - 1)];
  return bestBackup ?? PLAY_POOL.find((b) => b.id !== a.id)!;
}

/** Build the full deterministic pair sequence for a mode + seed. */
export function buildSequence(mode: Mode, seed: number): Pair[] {
  const rng = makeRng(seed >>> 0);
  const recent: string[] = [];
  const recentSet = new Set<string>();
  const seq: Pair[] = [];

  const remember = (id: string): void => {
    recent.push(id);
    recentSet.add(id);
    while (recent.length > RECENT) recentSet.delete(recent.shift()!);
  };

  for (let idx = 0; idx < MAX_PAIRS; idx++) {
    const stat = statForPair(mode, idx);
    const [rMin, rMax] = ratioBand(idx, STATS[stat].floor);

    // Pick A among countries with a usable value and not recently seen; retry a few
    // times so A has at least one in-band partner where possible.
    let a: Country | null = null;
    let b: Country | null = null;
    for (let attempt = 0; attempt < 30 && !b; attempt++) {
      const eligible = PLAY_POOL.filter((c) => !recentSet.has(c.id) && statValue(c, stat) > 0);
      const pool = eligible.length ? eligible : PLAY_POOL.slice();
      const cand = pool[randInt(rng, 0, pool.length - 1)];
      const valA = statValue(cand, stat);
      if (valA <= 0) continue;
      const partner = pickPartner(cand, valA, stat, rMin, rMax, recentSet, rng);
      if (partner && partner.id !== cand.id) {
        a = cand;
        b = partner;
      }
    }
    if (!a || !b) {
      // Deterministic last resort — should never trigger given the pool size.
      a = PLAY_POOL[randInt(rng, 0, PLAY_POOL.length - 1)];
      b = PLAY_POOL.find((c) => c.id !== a!.id && statValue(c, stat) !== statValue(a!, stat))!;
    }

    const valA = statValue(a, stat);
    const valB = statValue(b, stat);
    const aLeft = randInt(rng, 0, 1) === 0;
    seq.push({
      stat,
      leftId: aLeft ? a.id : b.id,
      rightId: aLeft ? b.id : a.id,
      leftVal: aLeft ? valA : valB,
      rightVal: aLeft ? valB : valA,
    });
    remember(a.id);
    remember(b.id);
  }
  return seq;
}

/** The single pair at a position (builds the sequence; used by tests/UI). */
export function pairAt(mode: Mode, seed: number, idx: number): Pair {
  return buildSequence(mode, seed)[idx];
}
